import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIconComponent, provideIcons } from "@ng-icons/core";
import { gameSightDisabled } from '@ng-icons/game-icons';
import { matDeleteSharp } from '@ng-icons/material-icons/sharp';
import {
  simpleVowpalwabbit,
} from '@ng-icons/simple-icons';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialog } from "primeng/confirmdialog";
import { catchError, EMPTY, Observable, tap, throwError } from 'rxjs';
import { ApiClientService } from '../../../../../services/apiclient/api-client.service';
import { Rabbit } from '../../../../../services/apiclient/Rabbit';
import { RoutingService } from '../../../../routing.service';

@Component({
  selector: 'app-rabbit-card',
  imports: [NgIconComponent, ConfirmDialog],
  templateUrl: './rabbit-card.component.html',
  styleUrl: './rabbit-card.component.css',

  providers: [
    provideIcons({
      matDeleteSharp,
      simpleVowpalwabbit,
      gameSightDisabled,
    }),
    ConfirmationService,
  ],
})
export class RabbitCardComponent {
  readonly rabbit = input.required<Rabbit>();

  readonly rabbitDeleted = output<Rabbit>();

  private readonly routingService = inject(RoutingService);
  private readonly apiClient = inject(ApiClientService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  private readonly destroyRef = inject(DestroyRef);

  async navigateToEdit(): Promise<void> {
    await this.routingService.navigateToEditRabbit(this.rabbit().id);
  }

  confirmDeleteRabbit(): void {
    this.confirmationService.confirm({
      closeOnEscape: true,
      dismissableMask: true,
      header: 'Bist du sicher?',
      message: `Möchtest du das Kaninchen "${this.rabbit().name}" wirklich löschen?`,
      acceptButtonProps: {
        label: 'Löschen',
        severity: 'danger',
      },
      rejectButtonProps: {
        label: 'Abbrechen',
        outlined: true,
      },
      accept: () => this.deleteRabbit(),
    });
  }

  deleteRabbit(): void {
    this.apiClient.deleteRabbits(this.rabbit().id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.rabbitDeleted.emit(this.rabbit())),
        catchError((error) => this.handleError(error)),
      )
      .subscribe();
  }

  private handleError(error: unknown): Observable<never> {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }

    switch (true) {
      case error.status >= 500 && error.status <= 599:
        this.messageService.add({
          severity: 'error',
          summary: 'Serverfehler',
          detail:
            'Beim Löschen des Kaninchens ist ein Fehler aufgetreten. Bitte versuche es erneut.',
        });
        break;
      case error.status === 400:
        console.error(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Clientfehler',
          detail:
            'Die Anwendung scheint falsche Daten an den Server zu senden.',
        });
        break;
      case error.status === 404:
        this.messageService.add({
          severity: 'error',
          summary: 'Kaninchen nicht gefunden',
          detail:
            'Das zu löschende Kaninchen wurde nicht gefunden. Möglicherweise wurde es bereits gelöscht.',
        });
        break;
      default:
        console.error(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Unerwarteter Fehler',
          detail:
            'Beim Löschen des Kaninchens hat der Server eine unerwartete Antwort zurückgegeben.',
        });
        break;
    }

    return EMPTY;
  }
}
